/**
 * @module AdapterStatusBadge
 * @package @geenius/adapters/solidjs-solidui
 * @description Renders the SolidUI adapter status badge for the SolidJS
 * SolidUI variant. The badge keeps the shared adapter status vocabulary while
 * exposing token-friendly SolidUI classes.
 */

import type { AdapterStatusType } from "@geenius/adapters";
import type { JSX } from "solid-js";
import { cx } from "../lib/cx";

const LABELS: Record<AdapterStatusType, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Error",
  initializing: "Initializing",
};

const BADGE_CLASSES: Record<AdapterStatusType, string> = {
  connected: "border-primary/20 bg-primary/10 text-primary",
  disconnected: "border-border bg-muted text-muted-foreground",
  error: "border-destructive/20 bg-destructive/10 text-destructive",
  initializing: "border-accent/20 bg-accent/10 text-accent",
};

const DOT_CLASSES: Record<AdapterStatusType, string> = {
  connected: "bg-primary",
  disconnected: "bg-muted-foreground",
  error: "bg-destructive",
  initializing: "bg-accent animate-pulse motion-reduce:animate-none",
};

/**
 * Props for rendering the SolidUI adapter status badge.
 *
 * @property status Shared adapter status vocabulary value to display.
 * @property showLabel Whether the human-readable label should be rendered.
 * @property size Visual size variant for the badge and status dot.
 * @property class Optional class name merged into the badge wrapper.
 */
export interface AdapterStatusBadgeProps {
  status: AdapterStatusType;
  showLabel?: boolean;
  size?: "sm" | "md";
  class?: string;
}

/**
 * Renders a compact SolidUI badge for the current adapter lifecycle state.
 *
 * @param props Badge props including the status token, optional label visibility, and size variant.
 * @returns A styled badge element that mirrors the shared adapter status taxonomy.
 */
export function AdapterStatusBadge(
  props: AdapterStatusBadgeProps,
): JSX.Element {
  const size = () => props.size ?? "sm";

  return (
    <span
      class={cx(
        "solidui-adapter-status inline-flex items-center gap-1.5 rounded-full border font-semibold uppercase transition-colors motion-reduce:transition-none",
        size() === "md" ? "px-3 py-1 text-xs" : "px-2.5 py-0.5 text-[0.625rem]",
        BADGE_CLASSES[props.status],
        props.class,
      )}
      data-status={props.status}
    >
      <span
        class={cx(
          "rounded-full",
          size() === "md" ? "h-2 w-2" : "h-1.5 w-1.5",
          DOT_CLASSES[props.status],
        )}
      />
      {props.showLabel !== false && <span>{LABELS[props.status]}</span>}
    </span>
  );
}
